<template>
  <Modal
    :model-value="modelValue"
    v-on:update:model-value="updateModelValue"
    :title="'Archive ' + truncateShort(instanceType) + ' ' + truncateShort(instance.name)"
  >
    <p>
      Are you sure you want to archive {{ truncateLong(instanceType) }}
      <strong>{{ truncateLong(instance.name) }}</strong
      >?
    </p>
    <template v-slot:footer="{ close }">
      <button
        class="button"
        v-on:click.prevent="close"
      >
        Cancel
      </button>
      <button
        autofocus
        class="button is-danger"
        :class="{ 'is-loading': loading }"
        :disabled="loading || undefined"
        v-on:click.prevent="performArchive"
      >
        Archive
      </button>
    </template>
  </Modal>
</template>

<script lang="ts">
import { isAxiosError } from 'axios'
import { mapActions } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import Modal from '@/components/Modal.vue'
import { errorParser } from '@/helpers'
import { truncateMixin } from '@/mixins'
import { useNotificationStore } from '@/stores'
import type { UUID } from '@/types'

export default defineComponent({
  components: {
    Modal,
  },
  mixins: [truncateMixin],
  props: {
    // The object to archive
    instance: {
      type: Object as PropType<{ id: UUID; name: string }>,
      required: true,
    },
    instanceType: {
      type: String,
      required: true,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    updateFunction: {
      type: Function as PropType<(id: UUID, payload: { archived: boolean }) => Promise<unknown>>,
      required: true,
    },
  },
  emits: {
    'update:modelValue': (value: boolean) => typeof value === 'boolean',
    archived: (value: boolean) => typeof value === 'boolean',
  },
  data: () => ({
    loading: false,
  }),
  methods: {
    ...mapActions(useNotificationStore, ['notify']),
    updateModelValue(value: boolean) {
      this.$emit('update:modelValue', value)
    },
    async performArchive() {
      if (this.loading) return
      this.loading = true
      try {
        await this.updateFunction(this.instance.id, { archived: true })
        this.$emit('archived', true)
        this.updateModelValue(false)
      } catch (err) {
        if (
          isAxiosError(err) &&
          err.response?.status === 400 &&
          err.response.data &&
          'archived' in err.response.data
        ) {
          err.response.data.archived.forEach((error: string) =>
            this.notify({ type: 'error', text: error }),
          )
        } else {
          this.notify({ type: 'error', text: errorParser(err) })
        }
      } finally {
        this.loading = false
      }
    },
  },
})
</script>
